import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';
import { 
  Store, Search, ShoppingCart, TrendingUp, 
  Star, Package, Filter, Grid, List 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MarketplacePage() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadMarketplace();
  }, [category, sort]);

  const loadMarketplace = async () => {
    try {
      setLoading(true);
      const [productsRes, storesRes] = await Promise.all([
        fetch(`${API_URL}/api/commerce/marketplace/products?category=${category}&sort=${sort}&limit=50`, {
          credentials: 'include'
        }),
        fetch(`${API_URL}/api/commerce/marketplace/stores?limit=20`, {
          credentials: 'include'
        })
      ]);

      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data.products || []);
      }

      if (storesRes.ok) {
        const data = await storesRes.json();
        setStores(data.stores || []);
      }
    } catch (error) {
      console.error('Failed to load marketplace:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!search.trim()) {
      loadMarketplace();
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/commerce/marketplace/products?search=${encodeURIComponent(search)}&sort=${sort}`, {
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    }
  };

  const addToCart = async (productId) => {
    try {
      const res = await fetch(`${API_URL}/api/commerce/cart/add?product_id=${productId}&quantity=1`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        alert('Added to cart!');
      } else {
        alert('Failed to add to cart');
      }
    } catch (error) {
      alert('Failed to add to cart');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Store className="w-8 h-8 text-blue-500" />
            Matrise Marketplace
          </h1>
          <p className="text-gray-600 mt-1">Discover products from verified merchants</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/commerce/cart')}>
            <ShoppingCart className="w-4 h-4 mr-2" />
            Cart
          </Button>
          <Button onClick={() => navigate('/commerce/create-store')} variant="outline">
            <Store className="w-4 h-4 mr-2" />
            Start Selling
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 flex gap-2">
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch}>
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="newest">Newest</option>
              <option value="popular">Most Popular</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </select>

            <div className="flex gap-2">
              <Button
                variant={view === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant={view === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Featured Stores */}
      {stores.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Featured Stores</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {stores.slice(0, 4).map(store => (
              <Card key={store.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/commerce/store/${store.id}`)}>
                <CardContent className="p-4 text-center">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-2">
                    {store.store_name[0]}
                  </div>
                  <h3 className="font-semibold">{store.store_name}</h3>
                  <div className="flex items-center justify-center gap-2 mt-2 text-sm text-gray-600">
                    <Package className="w-3 h-3" />
                    {store.total_sales} sales
                  </div>
                  <Badge variant="secondary" className="mt-2">{store.store_type}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Products */}
      <div>
        <h2 className="text-xl font-semibold mb-4">
          {search ? `Search Results (${products.length})` : 'All Products'}
        </h2>

        {loading ? (
          <p className="text-center py-8">Loading products...</p>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No products found</p>
              <Button className="mt-4" onClick={() => navigate('/commerce/create-store')}>
                Be the first to sell!
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className={view === 'grid' ? 'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4' : 'space-y-4'}>
            {products.map(product => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-gray-100 flex items-center justify-center">
                  {product.images?.[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package className="w-16 h-16 text-gray-400" />
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold line-clamp-1">{product.name}</h3>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{product.description}</p>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-lg font-bold text-blue-600">{product.price} {product.currency}</p>
                      <p className="text-xs text-gray-500">{product.store_name}</p>
                    </div>
                    {product.rating > 0 && (
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="w-4 h-4 text-yellow-500 fill-current" />
                        <span>{product.rating}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-4">
                    <Button
                      className="flex-1"
                      size="sm"
                      onClick={() => navigate(`/commerce/product/${product.id}`)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product.id);
                      }}
                    >
                      <ShoppingCart className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
